import { extend } from "@dhx/ts-common/core";
import { create, el } from "@dhx/ts-common/dom";
import { locate, locateNode } from "@dhx/ts-common/html";
import { createFactory, IState, ItemType, Navbar } from "@dhx/ts-navbar";
import { Position, tooltip } from "@dhx/ts-message";
import { IToolbarElement, IToolbar } from "./types";

export class Toolbar extends Navbar<IToolbarElement> implements IToolbar {
	constructor(element?: string | HTMLElement, config?) {
		super(element, extend({
			navigationType: "click"
		}, config));

		this._currentRoot = null;
		const render = () => this._draw();
		this.mount(element, create({ render }));
	}
	getState(): IState {
		const state = {};
		this.data.eachChild(this.data.getRoot(), item => {
			if ((item as any).twoState && !item.group) {
				state[item.id] = (item as any).active;
			} else if (item.type === ItemType.input || item.type === ItemType.selectButton) {
				state[item.id] = item.value;
			}
		}, false);
		for (const key in this._groups) {
			if (this._groups[key].active) {
				state[key] = this._groups[key].active;
			}
		}
		return state;
	}
	setState(state: IState): void {
		for (const key in state) {
			if (this._groups && this._groups[key]) {
				if (this._groups[key].active) {
					this.data.update(this._groups[key].active, { active: false });
					this._groups[key].active = state[key];
					this.data.update(state[key], { active: true });
				}
			} else {
				const item = this.data.getItem(key);
				if (item.type === ItemType.input || item.type === ItemType.selectButton) {
					this.data.update(key, { value: state[key] });
				} else {
					this.data.update(key, { active: state[key] });
				}
			}
		}
	}
	protected _customHandlers() {
		return {
			input: (e: Event) => {
				const id = locate(e);
				this.data.update(id, { value: (e.target as HTMLInputElement).value });
			},
			tooltip: (e: MouseEvent) => {
				const elem = locateNode(e);
				if (!elem) {
					return;
				}
				const id = elem.getAttribute("dhx_id");
				const item = this.data.getItem(id);
				if (item.tooltip) {
					tooltip(item.tooltip, {
						node: elem as HTMLElement,
						position: Position.bottom
					});
				}
			}
		};
	}
	protected _getFactory() {
		return createFactory({
			widget: this,
			defaultType: ItemType.navItem,
			allowedTypes: [
				ItemType.button,
				ItemType.customHTMLButton,
				ItemType.imageButton,
				ItemType.input,
				ItemType.selectButton,
				ItemType.separator,
				ItemType.spacer,
				ItemType.title,
				ItemType.navItem,
				ItemType.menuItem,
				ItemType.customHTML,
			],
			widgetName: "toolbar"
		});
	}
	protected _draw() {
		return el("nav.dhx_widget.dhx_toolbar", {
			class: (this.config.css ? this.config.css : "")
		}, [
				el("ul.dhx_navbar.dhx_navbar--horizontal", {
					dhx_widget_id: this._uid,
					tabindex: 0,
					onclick: this._handlers.onclick,
					onmousedown: this._handlers.onmousedown,
					oninput: this._handlers.input,
					onmouseover: this._handlers.tooltip,
					_hooks: {
						didInsert: node => {
							node.el.addEventListener("keyup", e => {
								if (e.which !== 9) {
									return;
								}
								const elem = locateNode(document.activeElement);
								if (!elem) {
									return;
								}
								const id = elem.getAttribute("dhx_id");
								const item = this.data.getItem(id);
								if (item.tooltip) {
									tooltip(item.tooltip, {
										node: elem as HTMLElement,
										position: Position.bottom,
										force: true
									});
								}
							}, true);
						}
					}
				}, this.data.map(item => this._factory(item), this.data.getRoot(), false))
			]);
	}
	protected _getMode(item, root): "right" | "bottom" {
		return item.id === root ? "bottom" : "right";
	}
	protected _close(e: MouseEvent): void {
		this._activePosition = null;
		this._currentRoot = null;
		super._close(e);
	}
	protected _setRoot(id: string) {
		if (this.data.getParent(id) === this.data.getRoot()) {
			this._currentRoot = id;
		}
	}
}